import * as React from "react";
import {
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  ExportCsv,
  ExportPrint,
  QuickFilter,
  QuickFilterControl,
  QuickFilterTrigger,
  QuickFilterClear,
  useGridApiContext,
} from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import {
  Box,
  Tooltip,
  Menu,
  MenuItem,
  Badge,
  Divider,
  TextField,
  InputAdornment,
} from "@mui/material";

import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";

export default function CustomGridToolbar({ exportComponent }) {
  const apiRef = useGridApiContext();

  // store the actual element, not just a boolean
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = React.useState(null);

  const handleOpenExportMenu = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchorEl(null);
  };

  const exportMenuOpen = Boolean(exportMenuAnchorEl);

  return (
    <Toolbar
      sx={{
        minHeight: 32,
        height: 32,
        py: 1,
        px: 0,
        display: "flex",
        alignItems: "center",
        width: 1,
        gap: 2,
        flexWrap: { xs: "wrap", sm: "nowrap" },
      }}
    >
      {/* LEFT: your custom exportComponent (ImportVariance / Export / Export All / etc.) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {exportComponent}
      </Box>

      {/* RIGHT: grid actions from MUI examples (Columns, Filters, Export menu, QuickFilter) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexGrow: 1,
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {/* Columns trigger */}
        <Tooltip title="Columns">
          <ColumnsPanelTrigger render={<ToolbarButton />}>
            <ViewColumnIcon fontSize="small" />
          </ColumnsPanelTrigger>
        </Tooltip>

        {/* Filters trigger with badge for active filters */}
        <Tooltip title="Filters">
          <FilterPanelTrigger
            render={(props, state) => (
              <ToolbarButton {...props} color="default">
                <Badge
                  badgeContent={state.filterCount}
                  color="primary"
                  variant="dot"
                >
                  <FilterListIcon fontSize="small" />
                </Badge>
              </ToolbarButton>
            )}
          />
        </Tooltip>

        {/* Export menu */}
        <Tooltip title="Export">
          <ToolbarButton
            id="export-menu-trigger"
            aria-controls="export-menu"
            aria-haspopup="true"
            aria-expanded={exportMenuOpen ? "true" : undefined}
            onClick={handleOpenExportMenu}
          >
            <FileDownloadIcon fontSize="small" />
          </ToolbarButton>
        </Tooltip>

        <Menu
          id="export-menu"
          anchorEl={exportMenuAnchorEl}
          open={exportMenuOpen}
          onClose={handleCloseExportMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            list: {
              "aria-labelledby": "export-menu-trigger",
            },
          }}
        >
          <ExportPrint render={<MenuItem />} onClick={handleCloseExportMenu}>
            Print
          </ExportPrint>
          <ExportCsv render={<MenuItem />} onClick={handleCloseExportMenu}>
            Download as CSV
          </ExportCsv>
          {/* If you have X Premium with ExportExcel you can add it here */}
        </Menu>

        {/* Quick filter (collapsible search) */}
        <QuickFilter>
          {/* The actual input field that expands */}
          <QuickFilterControl
            render={({ ref, ...controlProps }, state) => (
              <TextField
                {...controlProps}
                ownerState={{ expanded: state.expanded }}
                inputRef={ref}
                aria-label="Search"
                placeholder="Search..."
                size="small"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: state.value ? (
                      <InputAdornment position="end">
                        <QuickFilterClear
                          edge="end"
                          size="small"
                          aria-label="Clear search"
                          material={{ sx: { marginRight: -0.75 } }}
                        >
                          <CancelIcon fontSize="small" />
                        </QuickFilterClear>
                      </InputAdornment>
                    ) : null,
                    ...controlProps.slotProps?.input,
                  },
                  ...controlProps.slotProps,
                }}
              />
            )}
          />
        </QuickFilter>
      </Box>
    </Toolbar>
  );
}
